/**
 * Stale-bundle self-reload.
 *
 * The SPA has no other way to learn that a new deploy shipped: after the
 * first document load every navigation is client-side, and Android keeps
 * background tabs / installed-PWA windows frozen for days. A tab left open
 * therefore keeps running whatever bundle it loaded — old page designs, old
 * bugs — until the user happens to pull to refresh.
 *
 * Every page reloads onto a newer deploy once it knows of one (2026-09-25
 * 拍板: an old bundle looks like a bug; a reload is the lesser cost). It hears
 * of one from the X-App-Version the edge stamps on every API answer
 * (noteLiveVersion, read by apiFetch — the calls a page makes anyway), on
 * a return to the foreground or a back/forward-cache restore, when it asks
 * /__version itself, and on one clock of its own (2026-10-01 Riley 拍板): a
 * single site-wide timer asks /__version every STALE_POLL_INTERVAL_MS while the
 * page is visible. The clock is for the pages that make no calls after they
 * load — a room's page (its lines come over Stream), a closed room, the
 * Rooms list — which otherwise sat in the foreground on an old bundle for as
 * long as nobody left them. One timer per document, in this module, never
 * one per page. What a reload would lose is kept as it is typed or
 * picked (use-kept-across-reload) and put back by the page that comes up.
 * Only two things are not reloaded under: a send on its way ('wait',
 * seconds), and a voice clip being recorded ('warn': the page says so, and
 * reloads when it is done or WARN_SECONDS later).
 *
 * While a deploy switches over, old and new instances answer side by side
 * for a while (dev 2026-09-25 06:32:49–59: six reloads of one chat in ten
 * seconds, the page HTML from one instance, its calls from the other). So a
 * version is only believed after CONFIRM_MS, when /__version still says it;
 * a version this tab has already been served is older, never a reason; and
 * one target version gets at most one reload per LOOP_GUARD_MS.
 */

// A returning tab asks at most this often, so rapid app-switching does not
// turn into a stream of /__version hits.
const CHECK_INTERVAL_MS = 30_000;
// The site's one clock: how often a visible page asks /__version on its own.
// A few dozen bytes a time; the hidden page does not ask at all.
export const STALE_POLL_INTERVAL_MS = 120_000;
// How long a newly heard version must stand before the page reloads onto it:
// longer than Render's switch-over, when both instances answer.
const CONFIRM_MS = 30_000;
// One reload per target version in this long, whatever the instances say.
const LOOP_GUARD_MS = 120_000;
// This tab's history: the versions it has been served, and its last reload.
const SERVED_KEY = 'ember-stale-served';
const RELOADED_KEY = 'ember-stale-reloaded';
// How long a recording is given once the page has said a reload is coming.
export const STALE_RELOAD_WARN_SECONDS = 10;
// The deploy a document was served by, for the bundles whose shell carries no
// bootstrap — the marketing pages and the admin console (2026-10-01 拍板: every
// page of the site reloads onto a newer deploy, not only the product SPA). The
// edge stamps one <meta> of this name into every HTML it sends them
// (spa-shell appVersionMetaTag; the two names must agree). Those bundles have
// no apiFetch, so they hear of a deploy from /__version alone: on a return to
// the foreground and on the clock.
export const APP_VERSION_META_NAME = 'ember-app-version';

/** The served version off the document's <meta>, '' when there is none. */
export function readAppVersionMeta(): string {
  try {
    return String(document.querySelector(`meta[name="${APP_VERSION_META_NAME}"]`)?.getAttribute('content') || '').trim();
  } catch {
    return '';
  }
}

export type StaleReloadHold = 'wait' | 'warn';

const holds = new Map<symbol, StaleReloadHold>();
let served = '';
let everServed = new Set<string>();
let heard = '';
let confirmTimer: number | null = null;
let target = '';
let stale = false;
let reloading = false;
let warn: ((seconds: number, reloadNow: () => void) => void) | undefined;
let warnTimer: number | null = null;
let warnSpent = false;
let pollTimer: number | null = null;

function readSession(key: string): string {
  try { return window.sessionStorage?.getItem(key) || ''; } catch { return ''; }
}

function writeSession(key: string, value: string): void {
  try { window.sessionStorage?.setItem(key, value); } catch { /* the guards weaken to this page's memory */ }
}

function reload(): void {
  if (reloading) return;
  reloading = true;
  writeSession(RELOADED_KEY, JSON.stringify({ to: target, at: Date.now() }));
  window.location.reload();
}

/** A reload onto `version` already happened moments ago in this tab. */
function reloadedLately(version: string): boolean {
  try {
    const last = JSON.parse(readSession(RELOADED_KEY) || 'null') as { to?: string; at?: number } | null;
    return Boolean(last && last.to === version && Date.now() - Number(last.at || 0) < LOOP_GUARD_MS);
  } catch {
    return false;
  }
}

function settle(): void {
  if (!stale || reloading) return;
  const kinds = [...holds.values()];
  if (kinds.includes('wait')) return;
  if (kinds.includes('warn') && !warnSpent) {
    if (warnTimer === null) {
      warnTimer = window.setTimeout(() => { warnSpent = true; settle(); }, STALE_RELOAD_WARN_SECONDS * 1000);
      warn?.(STALE_RELOAD_WARN_SECONDS, reload);
    }
    return;
  }
  reload();
}

/** Hold the reload until the returned release is called. */
export function holdStaleReload(kind: StaleReloadHold = 'wait'): () => void {
  const token = Symbol('stale-reload-hold');
  holds.set(token, kind);
  return () => { if (holds.delete(token)) settle(); };
}

async function liveVersion(): Promise<string> {
  try {
    const res = await fetch('/__version', { cache: 'no-store' });
    if (!res.ok) return '';
    return String(((await res.json()) as { version?: string }).version || '').trim();
  } catch {
    return ''; // offline / transient — the next call or foreground return retries
  }
}

async function confirm(version: string): Promise<void> {
  confirmTimer = null;
  const live = await liveVersion();
  if (live !== version) {
    // Still switching, or gone again: whatever answers next starts over.
    heard = '';
    if (live) noteLiveVersion(live);
    return;
  }
  if (reloadedLately(version)) {
    // Reloaded onto it moments ago and still served the old page: the next
    // answer asks again, and after LOOP_GUARD_MS it is taken.
    heard = '';
    return;
  }
  target = version;
  stale = true;
  settle();
}

/** The deploy that answered a call (X-App-Version); a newer one reloads. */
export function noteLiveVersion(version: string | null | undefined): void {
  const live = String(version || '').trim();
  if (!served || !live || stale || everServed.has(live)) return;
  if (live === heard) return;
  heard = live;
  if (confirmTimer !== null) window.clearTimeout(confirmTimer);
  confirmTimer = window.setTimeout(() => { void confirm(live); }, CONFIRM_MS);
}

export interface StaleBundleReloadOptions {
  /** A recording holds the reload: say it is coming, `reloadNow` takes it. */
  warn?: (seconds: number, reloadNow: () => void) => void;
}

/**
 * Start watching for newer deploys. `currentVersion` is the bootstrap's
 * `appVersion` (the short commit the shell was rendered by); when it is
 * absent (local dev without git metadata) the watcher stays inert.
 */
export function initStaleBundleReload(
  currentVersion: string | null | undefined,
  options: StaleBundleReloadOptions = {},
): void {
  served = String(currentVersion || '').trim();
  heard = '';
  target = '';
  stale = false;
  reloading = false;
  if (confirmTimer !== null) window.clearTimeout(confirmTimer);
  if (warnTimer !== null) window.clearTimeout(warnTimer);
  if (pollTimer !== null) window.clearInterval(pollTimer);
  confirmTimer = null;
  warnTimer = null;
  pollTimer = null;
  warnSpent = false;
  if (!served) return;
  warn = options.warn;
  let history: string[] = [];
  try { history = JSON.parse(readSession(SERVED_KEY) || '[]') as string[]; } catch { history = []; }
  everServed = new Set([...(Array.isArray(history) ? history : []), served]);
  writeSession(SERVED_KEY, JSON.stringify([...everServed].slice(-20)));

  let lastCheckAt = 0;
  const check = async (): Promise<void> => {
    if (stale || reloading || document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastCheckAt < CHECK_INTERVAL_MS) return;
    lastCheckAt = now;
    noteLiveVersion(await liveVersion());
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  // bfcache restores skip the load path entirely — the page resumes with the
  // old bundle already running — so they need their own trigger.
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) void check();
  });
  // The clock. check() already stays quiet while the page is hidden, while a
  // reload is pending, and within CHECK_INTERVAL_MS of the last ask.
  pollTimer = window.setInterval(() => { void check(); }, STALE_POLL_INTERVAL_MS);
}
